/**
 * UnlockPanel — the "Ready · Locked" unlock moment (design-spec/05 Screen 03), adapted to the web
 * detail column (the phone's warm bottom-sheet becomes a warm card, DESIGN-NOTES delta d). Someone
 * did a favour; unlocking must feel like claiming a gift, never a hard paywall.
 *
 * The key-point bullets are ALWAYS free (proof of substance). The full letter + PDF are gated behind
 * the unlock. WP-F11a wires the REAL dual-path unlock (DESIGN-NOTES delta b):
 *   - a one-tap FREE-CREDIT unlock (sage) and/or a one-tap REFERRAL-BONUS unlock, when the
 *     unlock-options endpoint says they are available — no charge; and
 *   - a paid unlock via **Stripe Checkout redirect** (never IAP on web): mint a session, stash which
 *     reference it is for, and send the browser to Stripe's hosted page.
 * A one-tap success (or a 409 "already unlocked" from any path) bubbles up via {@link onUnlocked};
 * the parent reconciles + plays the seal ceremony. Price + copy are the canonical NZ$7.99 strings
 * (04-copy). This component NEVER mentions in-app purchases.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient, CandidateApiError } from '@/lib/api-client';
import { redirectTo, stashPendingUnlock } from '@/lib/unlock';
import type { ReferenceContent } from '@/lib/letter-content';

const PRICE = 'NZ$7.99';

/**
 * Whether an unlock/checkout failure means the reference is already unlocked server-side, so it should
 * reconcile as SUCCESS rather than error at the money moment (FE-M2). These endpoints only ever answer
 * 409 for an already-unlocked reference, so a bare HTTP 409 (`kind: 'conflict'`) counts even when the
 * ProblemDetails body carries no `already_unlocked` code — matching the mobile rail's status-based
 * folding. (`code` is kept as a belt-and-braces match.)
 */
function isAlreadyUnlocked(err: unknown): boolean {
  return err instanceof CandidateApiError && (err.kind === 'conflict' || err.code === 'already_unlocked');
}

function LockGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width={17} height={17} aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function GiftGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width={17} height={17} aria-hidden="true" className="flex-none">
      <path d="M20 12v8H4v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path
        d="M2 8h20v4H2zM12 8v12M12 8S10.5 4 8 4a2 2 0 000 4h4zm0 0s1.5-4 4-4a2 2 0 010 4h-4z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** A one-tap sage CTA for a free/referral unlock (no charge). */
function OneTapUnlock({
  label,
  hint,
  pending,
  disabled,
  onClick,
}: {
  label: string;
  hint: string;
  pending: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-md bg-sage-deep font-display text-[15px] font-semibold text-white shadow-btn-sage transition-colors duration-ui ease-thread hover:bg-sage-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:opacity-50"
      >
        <GiftGlyph />
        {pending ? 'Unlocking…' : label}
      </button>
      <p className="mt-1.5 text-center font-body text-[12px] font-medium text-sage-text">{hint}</p>
    </div>
  );
}

export function UnlockPanel({ content, onUnlocked }: { content: ReferenceContent; onUnlocked: () => void }) {
  const [notice, setNotice] = useState<string | null>(null);
  // A 422 means the credit/bonus vanished between the options read and the tap — hide that one-tap
  // path locally and let the paid rail carry on (never a dead button).
  const [freeExhausted, setFreeExhausted] = useState(false);
  const [referralExhausted, setReferralExhausted] = useState(false);
  const first = content.refereeName.split(/\s+/)[0] || content.refereeName;

  /** A one-tap failure: a 409 already-unlocked reconciles as success; 422 hides that path; else a notice. */
  function handleOneTapError(err: unknown, onExhausted: () => void, exhaustedCode: string) {
    if (isAlreadyUnlocked(err)) {
      onUnlocked();
      return;
    }
    if (err instanceof CandidateApiError && err.code === exhaustedCode) {
      onExhausted();
      setNotice('That free unlock was just used — you can still unlock below.');
      return;
    }
    setNotice('We couldn’t unlock just now. Try again in a moment.');
  }

  const freeUnlock = useMutation({
    mutationFn: () => apiClient.unlockWithFreeCredit(content.requestId),
    onMutate: () => setNotice(null),
    onSuccess: onUnlocked,
    onError: (err) => handleOneTapError(err, () => setFreeExhausted(true), 'no_free_credit'),
  });

  const referralUnlock = useMutation({
    mutationFn: () => apiClient.unlockWithReferralBonus(content.requestId),
    onMutate: () => setNotice(null),
    onSuccess: onUnlocked,
    onError: (err) => handleOneTapError(err, () => setReferralExhausted(true), 'no_referral_bonus'),
  });

  const checkout = useMutation({
    mutationFn: () => apiClient.startUnlockCheckout(content.requestId),
    onMutate: () => setNotice(null),
    onSuccess: (session) => {
      // Remember which reference this Stripe round-trip belongs to (the success/cancel URLs are
      // static server config and cannot carry it), then hand the whole page to Stripe.
      stashPendingUnlock(content.requestId);
      redirectTo(session.redirectUrl);
    },
    onError: (err) => {
      // A rail unlocked it between the options read and the tap → it's unlocked; reconcile as success.
      if (isAlreadyUnlocked(err)) {
        onUnlocked();
        return;
      }
      setNotice('We couldn’t start checkout just now. Try again in a moment.');
    },
  });

  const showFree = content.freeCreditAvailable && !freeExhausted;
  const showReferral = content.referralBonusAvailable && !referralExhausted;
  const busy = freeUnlock.isPending || referralUnlock.isPending || checkout.isPending;
  const hasOneTap = showFree || showReferral;

  return (
    <section
      aria-label="Unlock the full letter"
      className="rounded-xl border border-[rgba(200,137,42,0.4)] bg-gradient-to-b from-[#FEFBF3] to-[#FBF4E4] p-5 shadow-ready"
    >
      <h2 className="font-display text-[19px] font-semibold tracking-[-0.01em] text-ink">
        {first} recorded a reference for you.
      </h2>
      <p className="mt-1 font-body text-[12.5px] text-ink-quiet">Here’s what they touched on — free to read:</p>

      <ul className="mt-3 flex flex-col gap-2.5">
        {content.keyPoints.map((point) => (
          <li key={point} className="flex gap-2.5 font-body text-[13.5px] leading-snug text-ink">
            <svg viewBox="0 0 24 24" fill="none" width={17} height={17} aria-hidden="true" className="mt-0.5 flex-none">
              <path d="M5 13l4 4L19 7" stroke="#7A9278" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {point}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center gap-3 rounded-md border border-dashed border-line-2 bg-surface-tint px-3.5 py-3">
        <span className="grid h-9 w-9 flex-none place-items-center rounded-md bg-paper-2 text-gold-deep">
          <LockGlyph />
        </span>
        <span className="font-body text-[12.5px] font-medium text-ink">
          The full signed letter &amp; PDF
          <span className="mt-0.5 block font-normal text-ink-quiet">Yours to keep and share, forever</span>
        </span>
      </div>

      {/* One-tap free / referral unlocks (no charge) — the primary path when available. */}
      {showFree && (
        <OneTapUnlock
          label="Use your free unlock"
          hint="You have 1 free unlock · no charge"
          pending={freeUnlock.isPending}
          disabled={busy}
          onClick={() => freeUnlock.mutate()}
        />
      )}
      {showReferral && (
        <OneTapUnlock
          label="Use your referral unlock"
          hint="A friend’s invite earned you this · no charge"
          pending={referralUnlock.isPending}
          disabled={busy}
          onClick={() => referralUnlock.mutate()}
        />
      )}

      <div className="my-4 h-px bg-line" />

      <div className="flex items-baseline justify-between">
        <span className="font-display text-[15px] font-semibold text-ink">
          {hasOneTap ? 'Or unlock for keeps' : 'Unlock the full letter'}
        </span>
        <span className="font-display text-[19px] font-semibold text-ink">{PRICE}</span>
      </div>

      <button
        type="button"
        onClick={() => checkout.mutate()}
        disabled={busy}
        className="mt-3 flex h-12 w-full items-center justify-center rounded-md bg-gradient-to-br from-[#DAA43F] to-gold font-display text-[15px] font-semibold text-ink shadow-ready transition-[filter] duration-ui ease-thread hover:brightness-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper disabled:opacity-60"
      >
        {checkout.isPending ? 'Starting checkout…' : 'Unlock & keep as PDF'}
      </button>

      {notice ? (
        <p
          role="alert"
          className="mt-3 rounded-md bg-coral-tint px-3.5 py-2 text-center font-body text-[12px] font-medium text-coral-text"
        >
          {notice}
        </p>
      ) : (
        <p className="mt-3 text-center font-body text-[12px] font-medium text-gold-text">
          One-off. No subscription. Yours forever.
        </p>
      )}
    </section>
  );
}
